import { format, isAfter, isValid, parseISO } from "date-fns";
import React from "react";

// columns
import { Avatar } from "@performyard/py-components";

import AvatarGroup from "../../../../../../components/AvatarGroup";
import Button from "../../../../../../components/Button";
import Link from "../../../../../../components/Link";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

import { ReviewForm } from "../../../../../../api/services/searchServiceResultTypes/reviewForm";

// utils
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";

// css
import css from "../AllForms.module.scss";

const getMultiEmployeeTableColumnCell = (employees: Array<{ _id: string; name: string }>) => {
	if (employees[0]?._id === "_redacted") {
		return (
			<div className={css.userBox}>
				<Avatar size={20} dangerouslySetClassName={css.userAvatar} id={"REDACTED"} alt="Redacted user avatar" />
				<div className={css.userName}>
					<TruncatedText
						useTooltip="never"
						maxWidth={140}
						size="paragraph-3"
						color="primary-5"
						weight="regular"
						text={"Redacted"}
					/>
				</div>
			</div>
		);
	} else if (employees.length === 1) {
		const employee = employees[0];
		if (employee._id) {
			return (
				<Link href={toEmployeeUrl(employee._id)} forceHardReload={true}>
					<div className={css.userBox}>
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={employee._id}
							alt={`${employee.name}'s avatar`}
						/>
						<div className={css.userName}>
							<TruncatedText
								useTooltip="never"
								maxWidth={140}
								size="paragraph-3"
								color="primary-5"
								weight="regular"
								text={employee.name}
							/>
						</div>
					</div>
				</Link>
			);
		} else {
			return (
				<div className={css.userBox}>
					<Avatar
						size={20}
						dangerouslySetClassName={css.userAvatar}
						id={employee._id}
						alt={`${employee.name}'s avatar`}
					/>
					<div className={css.userName}>
						<TruncatedText
							useTooltip="never"
							maxWidth={140}
							size="paragraph-3"
							color="neutral-10"
							weight="regular"
							text={employee.name}
						/>
					</div>
				</div>
			);
		}
	} else if (employees.length > 1) {
		return (
			<div>
				<AvatarGroup
					employees={employees.map(({ _id, name }) => ({ _id, name }))}
					size={20}
					maxLength={5}
					hasLinks
				/>
			</div>
		);
	}
};

const getDateCell = (date: string) => {
	const parsedDate = parseISO(date);
	if (!isValid(parsedDate)) {
		return null;
	}

	return (
		<Typography size="paragraph-3" weight="regular">
			{format(parsedDate, "MMM dd, yyyy h:mm a")}
		</Typography>
	);
};

const getAllFormsTableCols = () => {
	return [
		{
			id: "action",
			label: "Action",
			width: 96,
			isSortable: false,
			getter: (form: ReviewForm) => {
				const { _id } = form;

				return <Button href={`/review/${_id}`}>Open Form</Button>;
			},
		},
		{
			id: "form_name",
			label: "Form",
			isSortable: true,
			width: 350,
			getter: (form: ReviewForm) => {
				const { form_name } = form;

				return (
					<TruncatedText
						useTooltip="never"
						canShowMore
						maxWidth={355}
						size="paragraph-3"
						weight="regular"
						text={form_name}
					/>
				);
			},
		},
		{
			id: "subject_name",
			label: "Subject",
			isSortable: true,
			width: 159,
			getter: (form: ReviewForm) => {
				const subjectID = form?.subject || "";
				const subjectName = form?.subject_name || "";

				return (
					<div className={css.userBox}>
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={subjectID}
							alt={`${subjectName}'s avatar`}
						/>
						<div className={css.userName}>
							<Link href={toEmployeeUrl(subjectID)} forceHardReload={true}>
								<TruncatedText
									useTooltip="never"
									maxWidth={140}
									size="paragraph-3"
									color="primary-5"
									weight="regular"
									text={subjectName}
								/>
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "author_name",
			label: "Authors",
			isSortable: true,
			width: 159,
			getter: (review: ReviewForm) => {
				const authors =
					review?.author_objects.length > 0
						? review?.author_objects
						: [
								{
									_id: "",
									name: "Awaiting Assignment",
								},
							];

				return getMultiEmployeeTableColumnCell(authors);
			},
		},
		{
			id: "signer_name",
			label: "Signers",
			isSortable: true,
			width: 159,
			getter: (review: ReviewForm) => {
				const signers =
					review?.signer_objects.length > 0
						? review?.signer_objects
						: [
								{
									_id: "",
									name: "Awaiting Assignment",
								},
							];

				return getMultiEmployeeTableColumnCell(signers);
			},
		},
		{
			id: "authors_due_date",
			label: "Author's Due Date",
			isSortable: true,
			width: 148,
			getter: (review: ReviewForm) => {
				const { authors_due_date } = review;

				return getDateCell(authors_due_date);
			},
		},
		{
			id: "signers_due_date",
			label: "Signer's Due Date",
			isSortable: true,
			width: 148,
			getter: (review: ReviewForm) => {
				const { signers_due_date } = review;

				return getDateCell(signers_due_date);
			},
		},
		{
			id: "complete_date",
			label: "Completed Date",
			isSortable: true,
			width: 148,
			getter: (review: ReviewForm) => {
				const { complete_date } = review;

				return getDateCell(complete_date);
			},
		},
		{
			id: "status",
			isSortable: false,
			label: "Status",
			width: 240,
			getter: (report) => {
				const {
					start_date,
					completed,
					submit,
					author_objects,
					signer_objects,
					author: currentAuthorID,
					signer: currentSignerID,
				} = report;
				const parsedStartDate = parseISO(start_date);
				const isRedacted = author_objects.length > 0 && author_objects[0]?._id === "_redacted";
				const today = new Date();

				let renderedText: string;

				if (completed) {
					renderedText = "Completed";
				} else if (isValid(parsedStartDate) && isAfter(parsedStartDate, today)) {
					renderedText = `Starting ${format(parsedStartDate, "MMM dd, yyyy")}`;
				} else if (submit) {
					const currentSigner = signer_objects.find((signerObject) => signerObject._id === currentSignerID);

					renderedText = `Awaiting sign off by ${currentSigner?.name}`;
				} else {
					const currentAuthor = author_objects.find((authorObject) => authorObject._id === currentAuthorID);
					const renderedString = isRedacted || !currentAuthor?.name ? "" : `by ${currentAuthor?.name}`;
					renderedText = `Awaiting Submission ${renderedString}`;
				}

				return <TruncatedText maxWidth={400} size="paragraph-3" weight="regular" text={renderedText} />;
			},
		},
	];
};

export default getAllFormsTableCols;
